import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { mockCars, type Car } from "../Listings";
import { Card, CardContent } from "../ui/card";
import { useLanguage } from '../../contexts/LanguageContext';

interface HowItWorksSectionProps {
  onCarBuy: (car: Car) => void;
}

export function HowItWorksSection({ onCarBuy }: HowItWorksSectionProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const steps = [
    {
      number: "1",
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.description'),
      icon: "🔍",
      hasAction: true,
      actionText: t('hero.browseCars')
    },
    {
      number: "2", 
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description'),
      icon: "📋",
      hasAction: true,
      actionText: t('common.getStarted')
    },
    {
      number: "3",
      title: t('howItWorks.step3.title'), 
      description: t('howItWorks.step3.description'),
      icon: "🔑",
      hasAction: false,
      actionText: t('common.learnMore')
    }
  ];

  const handleStepAction = (index: number) => {
    if (index === 0) {
      navigate('/listings');
    } else if (index === 1) {
      onCarBuy(mockCars[0]);
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('howItWorks.subtitle')}
          </p>
          <div className="w-24 h-1 bg-[#f91bdc] mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <Card 
              key={index} 
              className={`group transition-all duration-300 hover:shadow-xl border-[#f91bdc]/10 hover:border-[#f91bdc]/30 ${
                step.hasAction ? 'cursor-pointer' : ''
              }`}
              onClick={step.hasAction ? () => handleStepAction(index) : undefined}
            >
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#f91bdc] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  {step.number}
                </div>
                
                <div className="text-4xl mb-4">{step.icon}</div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {step.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
                
                {step.hasAction && (
                  <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-[#f91bdc] text-white px-4 py-2 rounded-lg text-sm inline-flex items-center">
                      <ArrowRight size={16} className="mr-2" />
                      {step.actionText}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}